// Time helpers for outreach. The contact policy is evaluated in
// America/Chicago: weekdays, 08:00 to 19:00. Only a time the patient chose
// themselves may fall outside that window (calls only, never texts).

import { parseApiTimestamp } from "@/lib/datetime"

export const POLICY_TZ = "America/Chicago"
export const DAY_START = "08:00"
export const DAY_END = "19:00"
export const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/

export function toMinutes(hhmm: string): number {
  const m = HHMM.exec(hhmm)
  if (!m) return NaN
  return Number(m[1]) * 60 + Number(m[2])
}

export function fromMinutes(mins: number): string {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

/** Clamp an HH:MM string into [min, max]. Invalid input returns `min`. */
export function clampTime(hhmm: string, min = DAY_START, max = DAY_END): string {
  const v = toMinutes(hhmm)
  if (Number.isNaN(v)) return min
  return fromMinutes(Math.min(Math.max(v, toMinutes(min)), toMinutes(max)))
}

/** 12-hour label, e.g. "09:05" -> "9:05 AM". */
export function formatHHMM(hhmm: string): string {
  const v = toMinutes(hhmm)
  if (Number.isNaN(v)) return hhmm
  const h = Math.floor(v / 60)
  const m = v % 60
  const suffix = h >= 12 ? "PM" : "AM"
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m).padStart(2, "0")} ${suffix}`
}

function tzOffsetMs(utcMs: number, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(utcMs))
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value)
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"))
  return asUtc - utcMs
}

/**
 * Convert a wall-clock date + time in `tz` to an ISO UTC string.
 * `date` is YYYY-MM-DD, `time` is HH:MM. DST-safe for real wall times.
 */
export function wallTimeToIso(date: string, time: string, tz = POLICY_TZ): string {
  const [y, mo, d] = date.split("-").map(Number)
  const [h, mi] = time.split(":").map(Number)
  const guess = Date.UTC(y, mo - 1, d, h, mi)
  let utc = guess - tzOffsetMs(guess, tz)
  // Second pass settles DST transitions between the guess and the answer.
  utc = guess - tzOffsetMs(utc, tz)
  return new Date(utc).toISOString()
}

/** "Thu, Sep 24, 10:30 AM CT" */
export function formatInTz(iso: string | null | undefined, tz = POLICY_TZ): string {
  if (!iso) return "-"
  const d = parseApiTimestamp(iso)
  if (!d) return iso
  const s = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d)
  return tz === POLICY_TZ ? `${s} CT` : `${s} (${tz})`
}

/** Monday = 0 … Sunday = 6, for a YYYY-MM-DD calendar date. */
export function weekdayOf(date: string): number {
  const [y, mo, d] = date.split("-").map(Number)
  return (new Date(Date.UTC(y, mo - 1, d)).getUTCDay() + 6) % 7
}

/** Whether a Central wall time falls in the policy window (weekday, 08:00-19:00). */
export function isPolicyDaytime(date: string, time: string): boolean {
  const t = toMinutes(time)
  return weekdayOf(date) < 5 && t >= toMinutes(DAY_START) && t < toMinutes(DAY_END)
}

export function describeWeekdays(days: number[]): string {
  const sorted = Array.from(new Set(days)).sort()
  if (sorted.length === 7) return "every day"
  if (sorted.length === 5 && sorted.every((d, i) => d === i)) return "Monday to Friday"
  return sorted.map((d) => WEEKDAY_LABELS[d]).join(", ")
}
